import { PlusOutlined, SearchOutlined, SyncOutlined, UploadOutlined } from '@ant-design/icons'
import { Button, Drawer, Tooltip, Input } from 'antd'
import qs from 'qs'
import { type FC, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { apiSyncLicensesInfo } from '~/api/adminApi'
import { PageHeader } from '~/components/PageHeader'
import SecureFormPostActionButton from '~/components/SecureFormPostActionButton'
import { useSoftwaresQuery } from '~/pages/query'
import { type License } from '~/types/license'
import { backendUrl, showErrors } from '~/utils/helpers'
import { LicenseForm } from './LicenseForm'
import { LicenseImportForm } from './LicenseImportForm'
import LicenseList from './LicenseList'

const Licenses: FC = () => {
  const [filterInput, setFilterInput] = useState<string | null>(null)
  const [edit, setEdit] = useState<License | null>(null)
  const [isDrawerOpen, setIsDrawerOpen] = useState(false)
  const [openLicenseImport, setOpenLicenseImport] = useState(false)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<Record<string, any>>({})
  const { data: softwares } = useSoftwaresQuery()
  const hasInitializedSoftwareFilter = useRef(false)

  useEffect(() => {
    if (softwares == null || hasInitializedSoftwareFilter.current) {
      return
    }

    const latestSoftwareIds = softwares
      .filter(software => software.is_latest)
      .map(software => software.id)

    hasInitializedSoftwareFilter.current = true
    if (latestSoftwareIds.length > 0) {
      setFilters(current => ({ ...current, software_id: latestSoftwareIds }))
    }
  }, [softwares])
  const downloadLink = useMemo(() => {
    let link = backendUrl('statistic/export-licenses')

    // Convert filters object to query string using qs
    const params = qs.stringify(filters, { arrayFormat: 'brackets', skipNulls: true })

    // Append params to the link
    if (params !== '') {
      link += `?${params}`
    }

    return link
  }, [filters])

  const syncLicenseInfoMutation = useMutation({
    mutationFn: apiSyncLicensesInfo,
    onError: showErrors,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['licenses'] })
    }
  })

  return (
    <>
      <PageHeader
        className="radup-page-header"
        title="Licenses"
        extra={[
          <Input.Search
            key={1}
            placeholder="Search for license or MAC"
            allowClear
            enterButton={<SearchOutlined />}
            onSearch={setFilterInput}
            style={{ width: 300 }}
          />,
          <Button key={2} type="primary" onClick={() => {
            setIsDrawerOpen(true)
          }}>
            <PlusOutlined /> New license
          </Button>,
          <SecureFormPostActionButton
            key="download"
            actionUrl={downloadLink}
            title="Export"
          />,
          <Button key="import_license" type="default" icon={<UploadOutlined />} onClickCapture={() => { setOpenLicenseImport(true) }}>
            Import
          </Button >,
          <Tooltip key='sync' title='Sync licenses status/info from Amazon'>
            <Button key={2} type="primary"
              loading={syncLicenseInfoMutation.isPending}
              onClick={() => {
                syncLicenseInfoMutation.mutate()
              }}
            >
              <SyncOutlined /> Sync
            </Button>
          </Tooltip>
        ]}
      />

      < LicenseList
        onEdit={item => {
          setEdit(item)
          setIsDrawerOpen(true)
        }}
        searchValue={filterInput}
        softwareFilter={filters.software_id ?? null}
        onFilter={(nextFilters) => {
          setFilters(nextFilters)
        }}
      />

      <Drawer
        open={isDrawerOpen}
        size={720}
        onClose={() => {
          setIsDrawerOpen(false)
          setEdit(null)
        }}
        destroyOnHidden
      >
        <LicenseForm
          license={edit}
          onSuccess={() => {
            setIsDrawerOpen(false)
            setEdit(null)
            queryClient.invalidateQueries({ queryKey: ['licenses'] })
          }} />
      </Drawer>

      <Drawer
        open={openLicenseImport}
        size={720}
        destroyOnHidden
        onClose={() => { setOpenLicenseImport(false) }}
      >
        <LicenseImportForm onSuccess={() => { setOpenLicenseImport(false) }} />
      </Drawer>
    </>
  )
}

export default Licenses
